import React, { useState } from "react";
import CardHeader from "./CardHeader/CardHeader";
import CardBody from "./CardBody/CardBody";
import GenericRadioGroup, { RadioGroupProps } from "../RadioGroup/GenericRadioGroup";
import { ReactComponent as ChevronUp } from "../../shared/assets/Icons/IconChevronUp.svg";
import { ReactComponent as ChevronDown } from "../../shared/assets/Icons/IconChevron-Down.svg";

/** Card variants representing different background themes. */
export type CardVariant = "primary" | "secondary" | "tertiary";

/** Tailwind utility classes for each variant. */
const variantClasses: Record<CardVariant, string> = {
  primary: `
    bg-semantic-bg-primary
    text-semantic-text-primary
    border border-semantic-border-primary
  `,
  secondary: `
    bg-semantic-bg-secondary
    text-semantic-text-secondary
    border border-semantic-border-secondary
  `,
  tertiary: `
    bg-semantic-bg-tertiary
    text-semantic-text-tertiary
    border border-semantic-border-tertiary
  `,
};

/** Available shadow levels for the card. */
export type CardShadow = "flat" | "sm" | "md";

/** Tailwind classes for the card shadow effects. */
const shadowClasses: Record<CardShadow, string> = {
  flat: "shadow-none",
  sm: "shadow-sm",
  md: "shadow-md",
};

/** Card props */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  shadow?: CardShadow;
  hasBorder?: boolean;
  isCollapsible: boolean;
  initialExpanded?: boolean;
  headerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  bodyContent?: React.ReactNode;
  radioOptions?: RadioGroupProps;
  theme?: "light" | "dark";
  children?: React.ReactNode;
  isLoading?: boolean;
  hoverable?: boolean;
  clickable?: boolean;
  actionsContent?: boolean;
}

/**
 * The main card component that can include a header, body, and additional content.
 */
export const GenericCard: React.FC<CardProps> = ({
  variant = "primary",
  shadow = "flat",
  hasBorder = true,
  isCollapsible = false,
  initialExpanded,
  headerContent,
  rightContent,
  bodyContent,
  radioOptions,
  children,
  isLoading,
  hoverable = false,
  clickable = false,
  onClick,
  actionsContent = false,
  ...props
}) => {

  // const [isExpanded, setIsExpanded] = useState(true);
  const [isExpanded, setIsExpanded] = useState(
    initialExpanded !== undefined ? initialExpanded : true
  );

  const toggleCollapse = () => {
    if(isCollapsible) {
      setIsExpanded((prev) => !prev);
    }
  };

  let finalClasses = `
    w-full
    flex flex-col
    rounded-sm
    transition-all duration-200 ease-out
    ${variantClasses[variant]}
    ${shadowClasses[shadow]}
    ${hasBorder ? "border" : ""}
  `;

    if (hoverable) {
      finalClasses += " transform hover:scale-105 hover:shadow-lg";
    }
    // add clickable
    if (clickable) {
      finalClasses += " cursor-pointer";
    }

    // Render a skeleton version if loading is true.
    if (isLoading) {
      return (
        <div className={finalClasses} {...props}>
          <div className="animate-pulse p-4">
            <div className="h-6 bg-gray-300 rounded w-1/2 mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded w-full"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-full"></div>
            </div>
          </div>
        </div>
      );
    }

  return (
    <div       
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={finalClasses} {...props}>
      {/* Header Section */}
      {/* {headerContent && <CardHeader onClick={toggleCollapse} hasDropdown>{headerContent}</CardHeader>} */}
      {headerContent && (
        <CardHeader
          onClick={toggleCollapse}
          hasDropdown={isCollapsible}
          rightContent={
            isCollapsible ? (
              actionsContent ? (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse();
                    }}
                    className="text-semantic-text-primary transition-transform"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  {rightContent}
                </>
              ) : (
                <>
                  {rightContent}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse();
                    }}
                    className="text-semantic-text-primary transition-transform"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </>
              )
            ) : (
              rightContent
            )
          }
        >
          {headerContent}
        </CardHeader>
      )}
      {/* Body Section */}
      <div
        className={`${
          isCollapsible && !isExpanded ? "overflow-hidden" : "overflow-visible"
        } transition-[max-height] duration-300 ${
          isExpanded ? "max-h-[1000px]" : "max-h-0"
        }`}
      >
        {bodyContent && <CardBody>{bodyContent}</CardBody>}
        {radioOptions && (
          <GenericRadioGroup
            options={radioOptions.options}
            defaultValue={radioOptions.defaultValue}
            onChange={radioOptions.onChange}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default GenericCard;
